import type { ScheduledPageCopy } from './scheduled-page-copy-types';

export const english: ScheduledPageCopy = {
  common: {
    close: 'Close',
    cancel: 'Cancel',
    save: 'Save changes',
    create: 'Create schedule',
    edit: 'Edit',
    retry: 'Try again',
    back: 'Back',
    next: 'Continue',
    loading: 'Loading',
    view: 'View',
    unknown: 'Unknown',
    notAvailable: 'Not available',
  },
  header: {
    navLabel: 'Scheduled',
    title: 'Scheduled',
    subtitle: 'Agent tasks that run unattended on your time.',
    timezone: 'Times shown in {timezone}',
    searchPlaceholder: 'Search schedules',
    newSchedule: 'New schedule',
    create: 'Create',
    createWithAgent: 'Create with agent',
    setUpManually: 'Set up manually',
    createWithAgentPrompt:
      'Help me set up a scheduled task. Explain how it works, then ask what I need and when. Show me a preview for confirmation.',
  },
  filters: {
    all: 'All',
    mine: 'Mine',
    shared: 'Shared',
    archived: 'Archived',
    filters: 'Filters',
    moreScopes: 'More schedule views',
    status: 'Status',
    anyStatus: 'Any status',
    active: 'Active',
    paused: 'Paused',
    completed: 'Completed',
  },
  calendar: {
    label: 'Scheduled agent calendar',
    emptyTitle: 'Plan your next scheduled task',
    more: '{count} more',
    truncated:
      'This range has more results. Narrow the dates or filters to see everything.',
    offlineTitle: 'You are offline',
    offlineDescription: 'Reconnect to load schedules and make changes.',
    malformed: 'This schedule could not be displayed safely.',
    loading: 'Loading calendar',
    allDay: 'Schedule timeline',
  },
  status: {
    upcoming: 'Upcoming',
    running: 'Running',
    waitingBackground: 'Waiting for background work',
    succeeded: 'Succeeded',
    failed: 'Failed',
    skipped: 'Skipped',
    needsAttention: 'Needs attention',
    unknownOutcome: 'Unknown outcome',
    canceled: 'Canceled',
    malformed: 'Unavailable',
    paused: 'Paused',
  },
  editor: {
    createTitle: 'New schedule',
    editTitle: 'Edit schedule',
    description: 'Set the task, timing, context, and unattended authority.',
    stepOf: 'Step {step} of 4',
    task: 'Task',
    timing: 'Frequency',
    execution: 'Details',
    review: 'Review',
    name: 'Schedule name',
    namePlaceholder: 'e.g. Monday performance brief',
    instructions: 'Agent instructions',
    instructionsPlaceholder:
      'Describe the result the agent should produce and where it may act.',
    sourceChat: 'Source chat',
    scheduleThisChat: 'Schedule this chat',
    sourceSnapshot:
      'A private snapshot is captured when you save. Later chat changes will not alter this schedule.',
    sourceUnavailable:
      'The source chat is unavailable or you no longer have access.',
    sourceChatLockedNote:
      'Schedules that reuse a source chat must keep persistent context and private visibility.',
    recurrence: 'Repeats',
    once: 'Once',
    hourly: 'Hourly',
    daily: 'Daily',
    weekdays: 'Weekdays',
    selectedWeekdays: 'Selected weekdays',
    weekly: 'Weekly',
    monthly: 'Monthly',
    date: 'Date',
    time: 'At',
    dayOfMonth: 'Day of month',
    whenPlaceholder: 'Tomorrow',
    whenInvalid: 'Enter a date, such as “tomorrow”.',
    end: 'Ends',
    never: 'Never',
    onDate: 'On a date',
    afterOccurrences: 'After runs',
    occurrences: 'Runs',
    nextRuns: 'Next runs',
    schedulePreview: 'Schedule preview',
    noEndDate: 'No end date.',
    runsUntil: 'Runs until {date}.',
    totalRuns: '{count} runs total.',
    resolvingTime: 'Checking this wall-clock time with the server…',
    serverTimeRequired:
      'The server must confirm this local time before saving.',
    contextMode: 'Thread context',
    persistent: 'Persistent context',
    persistentDescription: 'Reuse one schedule-owned thread across runs.',
    isolated: 'Fresh thread each run',
    isolatedDescription: 'Start every run with isolated context.',
    visibility: 'Visibility',
    private: 'Private',
    privateDescription:
      'Only you can view the full prompt, transcript, tool activity, and result.',
    workspace: 'Workspace',
    workspaceWarning:
      'Workspace sharing exposes the full prompt, transcript, tool activity, and result.',
    creditPolicy: 'Workspace credit policy',
    creditsAvailable:
      'Runs use the workspace’s existing credit balance and limits.',
    creditsUnavailable:
      'This workspace currently has no credits available. The schedule may pause at run time.',
    fullAuthorityTitle: 'Agent actions',
    fullAuthorityDescription:
      'At every run, the agent rechecks your current access and may perform destructive actions without asking first.',
    authorityAcknowledgment: 'Allow any action without asking first.',
    authorityRequired:
      'Allow agent actions to create this schedule. Runs are unattended, so nobody can approve them later.',
    recurrenceUnresolved:
      'This run time could not be confirmed. Pick the date and time again.',
    notifications: 'In-app notifications',
    notificationsDescription:
      'You will be notified once for terminal results that need your awareness.',
    reviewSummary: 'Review the exact task and cadence before creating it.',
    required: 'This field is required.',
    invalid: 'Enter a valid value.',
    past: 'Choose a time in the future.',
    endBeforeStart: 'End the schedule after its first run.',
    draftRestored: 'Restored your unsaved draft.',
    tooLong: 'This value is too long.',
    createDisabled:
      'You do not have permission to create schedules in this workspace.',
    saved: 'Schedule saved',
    unsavedTitle: 'Discard unsaved changes?',
    unsavedDescription:
      'Your changes to this schedule will be lost if you close now.',
    discardChanges: 'Discard changes',
  },
  detail: {
    title: 'Schedule details',
    cadence: 'Cadence',
    owner: 'Owner',
    timezone: 'Timezone',
    nextRun: 'Next run',
    lastRun: 'Last run',
    prompt: 'Task',
    sourceContext: 'Saved source context',
    executionMode: 'Execution mode',
    recentUsage: 'Usage',
    credits: 'credits',
    tools: 'actions',
    history: 'Run history',
    transcript: 'Conversation',
    resultFiles: 'Result files',
    noResultFiles: 'No result files were recorded for this run.',
    noRuns: 'No runs yet.',
    noTranscript: 'No transcript is available for this run yet.',
    toolActivity: 'Activity',
    toolInput: 'Task details',
    toolResult: 'Outcome',
    noToolActivity: 'No activity is available for this run.',
    loadMore: 'Load more',
    preview: 'Preview',
    sourceScheduled: 'Scheduled',
    sourceManual: 'Manual',
    sourceWatchdog: 'Recovery',
    roleUser: 'User',
    roleAssistant: 'Assistant',
    roleTool: 'Tool',
    roleSystem: 'System',
    runNow: 'Run now',
    pause: 'Pause',
    resume: 'Resume',
    share: 'Share with workspace',
    unshare: 'Make private',
    cancelRun: 'Cancel active run',
    archive: 'Archive',
    deleteSchedule: 'Delete schedule',
    openThread: 'View thread',
    confirmArchive: 'Archive this schedule?',
    confirmArchiveDescription:
      'Future work will be canceled. History and audit records are preserved.',
    confirmDelete: 'Delete this schedule?',
    confirmDeleteDescription:
      'This permanently removes the schedule. Past conversations and generated work stay in the workspace.',
    confirmUnshareDescription:
      'Workspace members will immediately lose access to the prompt, transcript, tool activity, and result.',
    confirmCancel: 'Cancel the active run?',
    confirmCancelDescription:
      'The run will stop at the next safe cancellation point.',
    actionCompleted: 'Schedule updated',
    permissionDenied:
      'You can view this schedule, but only its owner or a schedule administrator can change it.',
    needsAttention:
      'This run needs review before future unattended work continues.',
    runDetails: 'Run details',
  },
  notifications: {
    completed: 'Scheduled run completed',
    failed: 'Scheduled run failed',
    attention: 'Scheduled run needs attention',
    missed: 'Scheduled one-off was missed',
    viewRun: 'View run',
    empty: 'No run notifications yet.',
    unread: 'Unread',
    read: 'Read',
    caughtUp: "You're all caught up.",
  },
  errors: {
    generic: 'Something went wrong. Please try again.',
    permission: 'You do not have permission to perform this action.',
    disabled: 'Scheduled agents are not enabled for this workspace yet.',
    credits: 'The workspace does not have enough credits.',
    limit: 'This workspace has reached its active schedule limit.',
    runPending: 'This schedule already has a run pending or in progress.',
    invalidTime: 'Choose a valid future time.',
    notFound: 'This schedule or run is no longer available.',
  },
};
